// Phase 11.9 · Assignment Sets admin tab.
//
// Non-editable list of the 12 predefined sets. Each set is three
// concepts (one DS · one CS · one AI) sharing a single scenario.
// Admins can only Assign or Unassign a set — never create, edit or
// delete sets. A set can only be assigned when every one of its
// three concepts has an approved + active variant in the Scenario
// Bank (default or non-default); the per-concept amber dot shows
// which concept is blocking.
//
// Does not change the Scenario Bank, contracts, scoring, or any
// other admin tab.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Lock, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "../ui/button";

const ADMIN_HEADER = () => ({
  "X-Admin-Token": localStorage.getItem("iddl_admin_token") || "iddl-admin-dev-token",
});

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function fetchJson(path, init = {}) {
  const r = await fetch(`${API}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...ADMIN_HEADER(), ...(init.headers || {}) },
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body?.detail || `HTTP ${r.status}`);
  return body;
}

export default function AssignmentSets() {
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const d = await fetchJson("/admin/assignment_sets");
      setSets(d.sets || []);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function toggle(set) {
    const action = set.is_assigned ? "unassign" : "assign";
    setBusy(set.set_id);
    try {
      const d = await fetchJson(`/admin/assignment_sets/${set.set_id}/${action}`, { method: "POST" });
      // Patch in-place for immediate feedback.
      setSets((prev) => prev.map((s) => (s.set_id === set.set_id ? d.set : s)));
      toast.success(
        `${set.set_id} ${action === "assign" ? "assigned" : "unassigned"}`
      );
    } catch (e) {
      toast.error(e.message || `Failed to ${action} ${set.set_id}`);
    } finally {
      setBusy("");
    }
  }

  if (loading) {
    return (
      <div className="mt-8 p-6 label-mono text-[var(--iddl-muted)]" data-testid="assignment-sets-loading">
        <Loader2 className="inline mr-2 animate-spin" size={14} /> Loading sets…
      </div>
    );
  }

  if (err) {
    return (
      <div className="mt-8 p-4 border border-rose-300 bg-rose-50 text-rose-700 text-sm">
        {err}
      </div>
    );
  }

  const assignedCount = sets.filter((s) => s.is_assigned).length;

  return (
    <div className="mt-8" data-testid="assignment-sets">
      {/* ── overview strip ─────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap mb-6">
        <div className="label-mono text-[var(--iddl-muted)]">
          12 fixed sets · {assignedCount} assigned
        </div>
        <div className="ms-auto text-xs text-[var(--iddl-muted)] flex items-center gap-2">
          <Lock size={12} /> Sets are predefined and cannot be created, edited, or deleted
        </div>
      </div>

      {/* ── set grid ────────────────────────────────────────────── */}
      <div className="grid gap-4">
        {sets.map((s) => (
          <SetCard key={s.set_id} s={s} onToggle={() => toggle(s)} busy={busy === s.set_id} />
        ))}
      </div>
    </div>
  );
}

function SetCard({ s, onToggle, busy }) {
  const tone = s.is_assigned
    ? "border-emerald-500"
    : s.assignable
      ? "border-[var(--iddl-rule)]"
      : "border-amber-300";
  return (
    <div
      className={`paper p-5 border-2 ${tone}`}
      data-testid={`set-card-${s.set_id}`}
    >
      {/* header */}
      <div className="flex items-center gap-3 flex-wrap">
        <code className="label-mono font-semibold">{s.set_id}</code>
        <span className="font-display font-semibold">— {s.set_label}</span>
        {s.is_assigned && (
          <span className="label-mono px-2 py-0.5 border border-emerald-500 text-emerald-700 bg-emerald-50">
            ASSIGNED
          </span>
        )}
        {!s.is_assigned && !s.assignable && (
          <span
            className="label-mono px-2 py-0.5 border border-amber-400 text-amber-700 bg-amber-50 flex items-center gap-1"
            data-testid={`set-blocked-${s.set_id}`}
          >
            <AlertTriangle size={12} /> BLOCKED
          </span>
        )}
        <span className="label-mono text-[var(--iddl-muted)] ms-auto">
          Scenario: <code>{s.scenario_ref}</code>
        </span>
      </div>

      {/* three concepts */}
      <ul className="mt-4 grid md:grid-cols-3 gap-3">
        {s.concepts.map((c) => (
          <li
            key={c.concept_id}
            className="border border-[var(--iddl-rule)] p-3 text-sm min-w-0"
            data-testid={`set-concept-${s.set_id}-${c.concept_id}`}
          >
            <div className="flex items-center gap-2">
              <code className="label-mono font-semibold">{c.concept_id}</code>
              {c.is_approved ? (
                <CheckCircle2 size={14} className="text-emerald-600" />
              ) : (
                <span
                  title="No approved + active variant in the Scenario Bank"
                  data-testid={`concept-blocker-${s.set_id}-${c.concept_id}`}
                >
                  <AlertTriangle size={14} className="text-amber-500" />
                </span>
              )}
            </div>
            <div className="font-display font-semibold mt-1">
              {c.title || <span className="text-[var(--iddl-muted)]">—</span>}
            </div>
            <div className="label-mono text-[var(--iddl-muted)] mt-2 break-words">
              {c.domain || "—"}
              {c.subdomain && <> · {c.subdomain}</>}
            </div>
            <div className="label-mono text-[var(--iddl-muted)] mt-1 break-words">
              Primitive: <code>{c.primitive || "—"}</code>
            </div>
            {!c.is_approved && (
              <div className="label-mono text-amber-700 mt-2 break-words">
                No approved + active variant yet
              </div>
            )}
          </li>
        ))}
      </ul>

      {/* actions */}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <Button
          onClick={onToggle}
          disabled={busy || (!s.is_assigned && !s.assignable)}
          variant={s.is_assigned ? "outline" : "default"}
          className="rounded-none"
          data-testid={`set-toggle-${s.set_id}`}
        >
          {busy && <Loader2 className="mr-2 animate-spin" size={14} />}
          {s.is_assigned ? "Unassign set" : "Assign set"}
        </Button>
        {!s.is_assigned && !s.assignable && (
          <span className="text-xs text-amber-700" data-testid={`set-blocking-list-${s.set_id}`}>
            Blocked by: {s.blocking.join(", ")}
          </span>
        )}
      </div>
    </div>
  );
}
